import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, MoreVertical, Shield, Settings, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { AdminRole } from '@/types';

interface CompanyQueryResult {
  id: string;
  name: string;
  logo_url?: string;
  location?: string;
  website?: string;
  subscriptions: Array<{
    plan: string;
    status: string;
  }>;
  sso_configs: Array<{
    provider?: string;
    status: string;
  }>;
  jobs: Array<{
    count: number;
  }>;
  created_at: string;
}

interface Customer {
  company_id: string;
  company_name: string;
  subscription_plan: string;
  subscription_status: string;
  sso_enabled: boolean;
  sso_provider?: string;
  total_jobs: number;
  created_at: string;
  logo_url?: string;
  location?: string;
  website?: string;
}

export function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [ssoFilter, setSSoFilter] = useState('all');
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/login');
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('role, admin_role')
      .eq('id', user.id)
      .single();

    if (profile?.role !== 'admin') {
      navigate('/dashboard');
      toast.error('Unauthorized access');
      return;
    }

    const adminRole = profile?.admin_role as AdminRole;
    const canManageUsers = adminRole &&
      (adminRole === 'founder' || adminRole === 'admin');

    if (!canManageUsers) {
      navigate('/admin');
      toast.error('Insufficient permissions');
      return;
    }

    setAdminRole(adminRole);
    fetchCustomers();
  };

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          logo_url,
          location,
          website,
          subscriptions (
            plan,
            status
          ),
          sso_configs (
            provider,
            status
          ),
          jobs (count),
          created_at
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }

      const formattedCustomers: Customer[] = (data as CompanyQueryResult[]).map(item => ({
        company_id: item.id,
        company_name: item.name,
        subscription_plan: item.subscriptions?.[0]?.plan || 'unsubscribed',
        subscription_status: item.subscriptions?.[0]?.status || 'inactive',
        sso_enabled: item.sso_configs?.[0]?.status === 'active',
        sso_provider: item.sso_configs?.[0]?.provider,
        total_jobs: item.jobs[0]?.count || 0,
        created_at: item.created_at,
        logo_url: item.logo_url,
        location: item.location,
        website: item.website,
      }));

      // Additional data validation and logging
      console.log(`Fetched ${formattedCustomers.length} customers`);
      
      // Sort customers by creation date
      const sortedCustomers = formattedCustomers.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setCustomers(sortedCustomers);
    } catch (error) {
      console.error('Comprehensive error fetching customers:', {
        error,
        timestamp: new Date().toISOString()
      });
      toast.error('Failed to load customers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.company_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesPlan = planFilter === 'all' || customer.subscription_plan === planFilter;
    const matchesSSO = ssoFilter === 'all' || 
      (ssoFilter === 'enabled' && customer.sso_enabled) ||
      (ssoFilter === 'disabled' && !customer.sso_enabled);
    return matchesSearch && matchesPlan && matchesSSO;
  });

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise':
        return 'bg-purple-500';
      case 'business':
        return 'bg-blue-500';
      case 'starter':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      case 'expired':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Customers</h1>
        <Badge variant="secondary" className="text-sm">
          <Shield className="h-4 w-4 mr-1" />
          Admin Panel
        </Badge>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Subscription Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="business">Business</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={ssoFilter} onValueChange={setSSoFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder="SSO Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All SSO Status</SelectItem>
            <SelectItem value="enabled">SSO Enabled</SelectItem>
            <SelectItem value="disabled">SSO Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>SSO</TableHead>
              <TableHead>Total Jobs</TableHead>
              <TableHead>Customer Since</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-10">
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer) => (
                <TableRow key={customer.company_id}>
                  <TableCell className="font-medium">
                    {customer.company_name}
                  </TableCell>
                  <TableCell>
                    <Badge className={getPlanBadgeColor(customer.subscription_plan)}>
                      {customer.subscription_plan.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusBadgeColor(customer.subscription_status)}>
                      {customer.subscription_status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {customer.sso_enabled ? (
                      <Badge variant="default" className="space-x-1">
                        <Key className="h-3 w-3" />
                        <span>{customer.sso_provider?.toUpperCase()}</span>
                      </Badge>
                    ) : (
                      <Badge variant="secondary">DISABLED</Badge>
                    )}
                  </TableCell>
                  <TableCell>{customer.total_jobs}</TableCell>
                  <TableCell>
                    {new Date(customer.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {adminRole === 'founder' || adminRole === 'admin' ? (
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/customers/${customer.company_id}/subscription`)}
                            className="space-x-2"
                          >
                            <Settings className="h-4 w-4" />
                            <span>Manage Subscription</span>
                          </DropdownMenuItem>
                        ) : null}
                        {adminRole === 'founder' || adminRole === 'admin' ? (
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/customers/${customer.company_id}/sso`)}
                            className="space-x-2"
                          >
                            <Key className="h-4 w-4" />
                            <span>Configure SSO</span>
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}